import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { apyToApr, MSOL_MINT, NATIVE_SOL, XSTEP_MINT } from "@stepfinance/common";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CellDefault,
  DEFAULT_EMPTY_CELL,
  formatBalance,
  formatPercentage,
  getColumnSearchProps,
  StepAssetBreakdownPopoverChip,
  StepButtonDropdown,
  StepPopoverCellWrapper,
  StepTable,
  TagInfo,
  TagInfoContainer,
} from "@stepfinance/react";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { Dispatch, FC } from "react";
import React, { useEffect, useMemo, useState } from "react";

import { JITO_SOL_MINT } from "../../../actions/jito/stake";
import { useNetworkConfig } from "../../../contexts/app/config";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useLendingContext } from "../../../contexts/lending";
import { useLiquidity } from "../../../contexts/liquidity";
import { useMarkets } from "../../../contexts/market";
import { useNavigationContext } from "../../../contexts/navigation";
import { useStakingContext } from "../../../contexts/staking";
import { useXStepContext } from "../../../contexts/xStep";
import { useCalculatePriceForDisplayCurrencyNumeric } from "../../../hooks";
import useLidoStaking from "../../../hooks/staking/useLidoStaking";
import { useScnSolAPY } from "../../../hooks/staking/useSoceanStaking";
import { useTulipStaking } from "../../../hooks/staking/useTulipStaking";
import { R_BASIS_MINT, useBasisMarkets } from "../../../hooks/tokens/useBasisMarkets";
import { STSOL_MINT } from "../../../models/lido";
import { SCN_MINT } from "../../../models/socean";
import { STAKED_TULIP_MINT } from "../../../models/tulip";
import { getTokenBySymbol, getTokenName } from "../../../utils/tokenInfo";
import { deepClone, isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { NavigationLink, PAGE_SIZE_OPTIONS, StepPopoverAssetWithIconRow, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import type { StakeActionOption } from "./Balances";
import { tokenSymbolSearchGetter } from "./Balances";

export const YIELD_BEARING_MINTS: Set<string> = new Set([
  XSTEP_MINT.toBase58(),
  MSOL_MINT.toBase58(),
  SCN_MINT.toBase58(),
  STSOL_MINT.toBase58(),
  R_BASIS_MINT.toBase58(),
  STAKED_TULIP_MINT.toBase58(),
  JITO_SOL_MINT.toBase58(),
]);

interface YieldBearingAssetsProps {
  yieldBearingTokens: NormalizedTokenAccount[];
  yieldBearingAssetsBalanceUSD: number | null;
  setSendAccount: Dispatch<React.SetStateAction<NormalizedTokenAccount | null>>;
  setStakingOption: Dispatch<React.SetStateAction<StakeActionOption | null>>;
  setBurnAccount: Dispatch<React.SetStateAction<NormalizedTokenAccount | null>>;
}

export interface YieldDataMap {
  [mint: string]: {
    apr: number;
    xPerToken: number;
    yieldingSymbol: string;
  };
}

export const YieldBearingAssets: FC<YieldBearingAssetsProps> = ({
  yieldBearingTokens,
  yieldBearingAssetsBalanceUSD,
  setSendAccount,
  setStakingOption,
  setBurnAccount,
}) => {
  const { connected, watching } = useEnrichedWallet();
  const { navigate } = useNavigationContext();
  const { connection } = useConnection();
  const { network } = useNetworkConfig();
  const isMainnet = network === "mainnet-beta";
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [pendingTx, setPendingTx] = useState("");
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "YieldBearingAssetsPaginationSize",
    PAGE_SIZE_OPTIONS.s[0],
  );

  const calculatePriceForDisplayCurrencyNumeric = useCalculatePriceForDisplayCurrencyNumeric();

  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: { balances: balancesStrings },
        },
      },
    },
  } = useLanguageContext();

  const { marketDataMap, marketsInitialised, priceInUSD } = useMarkets();
  const { apr: xStepAPR, stepPerXStep } = useXStepContext();
  const { marinadeState } = useLiquidity();
  const { rBasisAPR, rBasisRatio } = useBasisMarkets();
  const {
    platforms: {
      solend: { mainReserveStats, yieldDataMap: solendYieldDataMap, solendCTokenMints },
    },
  } = useLendingContext();
  const [yieldDataMap, setYieldDataMap] = useState<YieldDataMap>({});

  const { lidoAPR } = useLidoStaking();
  const { apr: tulipAPR, ratio: tulipRatio } = useTulipStaking();
  const scnSolAPY = useScnSolAPY();

  const { solStakingAPYMap } = useStakingContext();

  // FIXME: this is buried logic and should live in useTokenAccounts context.
  // this implementation is currently not accurately displaying yeidlBearingAssetBalance
  const columnData = useMemo(
    () =>
      yieldBearingTokens
        .map((token) => {
          const result = deepClone({
            ...token,
            ...yieldDataMap[token.info.mint],
          });

          if (solendCTokenMints.includes(token.info.mint)) {
            // Find the real estimated price of a cToken by multiplying by xPerToken

            result.tokenAmount.priceUSD = result.xPerToken ? result.tokenAmount.priceUSD * result.xPerToken : 0;
            result.tokenAmount.balanceInUSD = result.tokenAmount.uiAmount * result.tokenAmount.priceUSD;
          }

          return result;
        })
        .sort((a, b) => b.tokenAmount.balanceInUSD - a.tokenAmount.balanceInUSD),
    [solendCTokenMints, yieldBearingTokens, yieldDataMap],
  );

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(columnData.length, PAGE_SIZE_OPTIONS.s),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.s,
      pageSize: parseInt(paginationSetting),
    });
  }, [columnData.length, paginationSetting]);

  useEffect(() => {
    if (marketsInitialised && isMainnet) {
      const scnPrice = priceInUSD(SCN_MINT.toBase58()) ?? 0;
      const stSOLPrice = priceInUSD(STSOL_MINT.toBase58()) ?? 0;
      const solPrice = priceInUSD(NATIVE_SOL.toBase58()) ?? 0;
      const scnSolAPR = apyToApr(scnSolAPY) ?? 0;
      const jitoSOLPrice = priceInUSD(JITO_SOL_MINT.toBase58()) ?? 0;

      setYieldDataMap({
        [XSTEP_MINT.toBase58()]: {
          apr: xStepAPR,
          xPerToken: stepPerXStep,
          yieldingSymbol: "STEP",
        },
        [MSOL_MINT.toBase58()]: {
          apr: (marinadeState?.apy ?? 0) * 100,
          xPerToken: marinadeState?.mSOLPricePerSOL ?? 0,
          yieldingSymbol: "SOL",
        },
        [SCN_MINT.toBase58()]: {
          apr: scnSolAPR,
          xPerToken: scnPrice / solPrice,
          yieldingSymbol: "SOL",
        },
        [STSOL_MINT.toBase58()]: {
          apr: lidoAPR,
          xPerToken: stSOLPrice / solPrice,
          yieldingSymbol: "SOL",
        },
        [R_BASIS_MINT.toBase58()]: {
          apr: rBasisAPR || 0,
          xPerToken: rBasisRatio || 0,
          yieldingSymbol: "BASIS",
        },
        [STAKED_TULIP_MINT.toBase58()]: {
          apr: tulipAPR,
          xPerToken: tulipRatio,
          yieldingSymbol: "TULIP",
        },
        [JITO_SOL_MINT.toBase58()]: {
          apr: solStakingAPYMap ? apyToApr(solStakingAPYMap["jito"] * 100) : 0,
          xPerToken: jitoSOLPrice / solPrice,
          yieldingSymbol: "SOL",
        },
        ...solendYieldDataMap,
      });
    }
  }, [
    connection,
    isMainnet,
    marinadeState?.apy,
    marinadeState?.mSOLPricePerSOL,
    marketsInitialised,
    priceInUSD,
    stepPerXStep,
    xStepAPR,
    lidoAPR,
    tulipAPR,
    tulipRatio,
    mainReserveStats,
    solendYieldDataMap,
    rBasisAPR,
    rBasisRatio,
    scnSolAPY,
    solStakingAPYMap,
  ]);

  const columns: StepColumnsType<NormalizedTokenAccount> = [
    {
      title: common.asset,
      dataIndex: "symbol",
      key: "symbol",
      width: 272,
      ...getColumnSearchProps(tokenSymbolSearchGetter, "assets"),
      filteredValue: filteredInfo.symbol ?? null,
      render(_, record) {
        const {
          info: { mint },
        } = record;
        return (
          <AssetCell>
            <TokenIconContainer>
              <TokenIcon mintAddress={mint} />
              <TokenIconText bold>
                <NavigationLink
                  onClick={() =>
                    window.open(`https://analytics.step.finance/defionsolana/token?mint=${mint}`, "_blank")
                  }
                >
                  {getTokenName(mint)}
                </NavigationLink>
              </TokenIconText>
            </TokenIconContainer>
            <StepButtonDropdown
              buttonTitle={balancesStrings.sendButton}
              onButtonClick={() => setSendAccount(record)}
              items={[
                {
                  label: balancesStrings.sendButton,
                  hidden: !(connected && !watching),
                  onClick: () => setSendAccount(record),
                },
                {
                  label: common.unstake,
                  hidden: !(connected && !watching && mint === MSOL_MINT.toBase58()),
                  onClick: () => setStakingOption({ action: "unstake", option: "Marinade" }),
                },
                {
                  label: common.unstake,
                  hidden: !(connected && !watching && mint === SCN_MINT.toBase58()),
                  onClick: () => setStakingOption({ action: "unstake", option: "Socean" }),
                },
                {
                  label: common.unstake,
                  hidden: !(connected && !watching && mint === STSOL_MINT.toBase58()),
                  onClick: () => setStakingOption({ action: "unstake", option: "Lido" }),
                },
                {
                  label: common.unstake,
                  hidden: !(connected && !watching && mint === JITO_SOL_MINT.toBase58()),
                  onClick: () => setStakingOption({ action: "unstake", option: "Jito" }),
                },
                {
                  label: common.unstake,
                  hidden: !(connected && !watching && mint === XSTEP_MINT.toBase58()) || pendingTx === "xstep",
                  onClick: async () => {
                    navigate(`/unstake`);
                  },
                },
                {
                  label: common.burnToken,
                  hidden: !(connected && !watching),
                  onClick: () => setBurnAccount(record),
                },
              ]}
            />
          </AssetCell>
        );
      },
      // FIXME: this is repeated logic in SpotAssets. DRY this shit up!
      sorter: (a, b) => {
        let strA = getTokenName(a.info.mint);
        let strB = getTokenName(b.info.mint);
        if (strA === undefined) {
          strA = a.info.mint;
        }
        if (strB === undefined) {
          strB = b.info.mint;
        }
        return strA.localeCompare(strB);
      },
    },
    {
      title: common.amount,
      dataIndex: "balance",
      key: "balance",
      className: "column-number",
      render(_, record) {
        return (
          <CellValue cellType="number">
            {formatBalance(record.tokenAmount.uiAmount, record.tokenAmount.decimals)}
          </CellValue>
        );
      },
      sorter: (a, b) => a.tokenAmount.uiAmount - b.tokenAmount.uiAmount,
    },
    {
      title: headers.price24h,
      dataIndex: "tokenPrice",
      key: "tokenPrice",
      className: "column-number",
      render(_, record) {
        const {
          tokenAmount: { priceUSD },
        } = record;

        if (priceUSD === null) {
          return (
            <PriceCell>
              <CellDefault />
            </PriceCell>
          );
        }

        const change24hPrice = calculatePriceForDisplayCurrencyNumeric(
          marketDataMap.get(record.info.mint)?.change24h ?? 0,
        );
        const currentPrice = calculatePriceForDisplayCurrencyNumeric(priceUSD);
        const change24hPct = ((currentPrice - change24hPrice) / change24hPrice) * 100;
        return (
          <PriceCell>
            <CellValue cellType="value">{priceUSD}</CellValue>
            <TagInfoContainer>
              <TagInfo tagType="performance" performanceNumber={change24hPct}>
                {formatPercentage(change24hPct / 100, { capped: true, performance: true })}
              </TagInfo>
            </TagInfoContainer>
          </PriceCell>
        );
      },
      sorter: (a, b) => (a.tokenAmount.priceUSD ?? 0) - (b.tokenAmount.priceUSD ?? 0),
    },
    {
      title: "APR",
      dataIndex: "apr",
      key: "apr",
      width: 110,
      className: "column-number",
      render(_, record) {
        const yieldInfo = yieldDataMap[record.info.mint];
        return <CellValue cellType="percentage">{yieldInfo?.apr / 100}</CellValue>;
      },
      sorter: (a, b) => {
        const yieldInfoA = yieldDataMap[a.info.mint];
        const yieldInfoB = yieldDataMap[b.info.mint];
        return yieldInfoA?.apr - yieldInfoB?.apr;
      },
    },
    {
      title: common.value,
      dataIndex: "balanceInUSD",
      key: "balanceInUSD",
      className: "column-number",
      render(_, record) {
        const {
          tokenAmount: { decimals, balanceInUSD, uiAmount },
        } = record;
        const yieldInfo = yieldDataMap[record.info.mint];
        if (!yieldInfo || balanceInUSD === null) {
          return <CellDefault />;
        }
        const { xPerToken, yieldingSymbol } = yieldInfo;
        const assetAmount = xPerToken !== null ? formatBalance(uiAmount * xPerToken, decimals) : DEFAULT_EMPTY_CELL;
        return (
          <TotalValueCell>
            <StepPopoverCellWrapper>
              <StepAssetBreakdownPopoverChip
                title={balancesStrings.yieldBearingAssets.assetPopoverTitle}
                content={
                  <StepPopoverAssetWithIconRow
                    tokenIconMintAddress={getTokenBySymbol(yieldingSymbol)?.address}
                    amount={assetAmount}
                    symbol={yieldingSymbol}
                  />
                }
                assetValue={balanceInUSD ?? 0}
                assetValueDisplay={
                  <CellValue cellType="userPositionValue" fontWeight="bold">
                    {balanceInUSD}
                  </CellValue>
                }
              />
            </StepPopoverCellWrapper>
          </TotalValueCell>
        );
      },
      sorter: (a, b) => {
        if ((a.tokenAmount.balanceInUSD ?? 0) - (b.tokenAmount.balanceInUSD ?? 0) === 0) {
          return a.tokenAmount.uiAmount - b.tokenAmount.uiAmount;
        }
        return (a.tokenAmount.balanceInUSD ?? 0) - (b.tokenAmount.balanceInUSD ?? 0);
      },
      defaultSortOrder: "descend",
    },
  ];

  return yieldBearingTokens.length > 0 && Object.keys(yieldDataMap).length > 0 && marketsInitialised ? (
    <YieldBearingAssetContainer>
      <ChevronCollapse
        isCollapseChild
        title={balancesStrings.yieldBearingAssets.title}
        value={yieldBearingAssetsBalanceUSD}
      >
        <StepTable<NormalizedTokenAccount>
          columns={columns}
          dataSource={columnData}
          rowKey={(record) => record.info.mint}
          pagination={pagination}
          onChange={(pagination, filters) => {
            setPagination(pagination);
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.s[0]);
            setFilteredInfo(filters);
          }}
        />
      </ChevronCollapse>
    </YieldBearingAssetContainer>
  ) : (
    <></>
  );
};

const PriceCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const AssetCell = styled.div`
  display: flex;
  align-items: center;
`;

const TokenIconContainer = styled.div`
  margin-right: 80px;
  width: 100px;
  display: flex;
  align-items: center;
`;

const YieldBearingAssetContainer = styled.div`
  margin-top: 20px;

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const TotalValueCell = styled.div`
  white-space: nowrap;
`;
